import { useEffect, useState } from "react";

import { toast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";

export interface UnidentifiedPayment {
  id: number;
  txInternalId: string | null;
  amount: number;
  createdAt: string;
  paymentMethod: string;
  rut: string | null;
  name: string | null;
}

export const useUnidentifiedPayments = () => {
  const [payments, setPayments] = useState<UnidentifiedPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalAmount, setTotalAmount] = useState(0);

  const fetchUnidentifiedPayments = async () => {
    setLoading(true);
    try {
      // Fetch wire transfer payments between 250,000 and 500,000
      // with no assigned client or sales request
      const { data, error } = await supabase
        .from("payments")
        .select("*")
        .eq("payment_method", "wire")
        .is("client", null)
        .is("sales_request", null)
        .gte("amount", 250000)
        .lte("amount", 500000)
        .order("created_at", { ascending: false });

      if (error) throw error;

      const formattedPayments: UnidentifiedPayment[] = data.map((payment) => ({
        id: payment.id,
        txInternalId: payment.tx_internal_id,
        amount: payment.amount,
        createdAt: payment.created_at,
        paymentMethod: payment.payment_method || "No especificado",
        rut: payment.rut,
        name: payment.name,
      }));

      setPayments(formattedPayments);
      // Calculate total amount
      const total = formattedPayments.reduce(
        (sum, payment) => sum + payment.amount,
        0
      );
      setTotalAmount(total);
    } catch (error) {
      console.error("Error fetching unidentified payments:", error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los pagos no identificados",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const assignPayment = async (
    paymentId: number,
    clientId: string,
    salesRequestId?: string
  ) => {
    try {
      // First update the payment with the client
      const { error: paymentError } = await supabase
        .from("payments")
        .update({ client: clientId, sales_request: salesRequestId })
        .eq("id", paymentId);

      if (paymentError) throw paymentError;

      // If there is a sales request, check if the payment covers the total amount
      if (salesRequestId && salesRequestId !== "none-selected") {
        // Get the sales request total amount
        const { data: salesRequestData, error: salesRequestFetchError } =
          await supabase
            .from("sales_request")
            .select("total_amount")
            .eq("id", salesRequestId)
            .single();

        if (salesRequestFetchError) throw salesRequestFetchError;

        const totalAmount = salesRequestData.total_amount;

        // Get all payments assigned to this sales request
        const { data: paymentsData, error: paymentsError } = await supabase
          .from("payments")
          .select("amount")
          .eq("sales_request", salesRequestId);

        if (paymentsError) throw paymentsError;

        // Calculate the sum of all payments
        const totalPaid = paymentsData.reduce(
          (sum, payment) => sum + payment.amount,
          0
        );

        // Mark as paid only if the payments cover the full amount
        const shouldMarkAsPaid = totalPaid >= totalAmount;

        console.log(
          `Sales request ${salesRequestId}: Total amount ${totalAmount}, Total paid ${totalPaid}, Should mark as paid: ${shouldMarkAsPaid}`
        );

        if (shouldMarkAsPaid) {
          const { error: updateError } = await supabase
            .from("sales_request")
            .update({ paid: true })
            .eq("id", salesRequestId);

          if (updateError) throw updateError;
        }
      }

      // Refresh the list
      await fetchUnidentifiedPayments();

      toast({
        title: "Éxito",
        description: "Pago asignado correctamente",
      });
    } catch (error) {
      console.error("Error assigning payment:", error);
      toast({
        title: "Error",
        description: "No se pudo asignar el pago",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    fetchUnidentifiedPayments();
  }, []);

  return {
    payments,
    loading,
    totalAmount,
    assignPayment,
    refreshPayments: fetchUnidentifiedPayments,
  };
};
